import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon, DocumentDuplicateIcon, MagnifyingGlassIcon,
  ArrowUpIcon, ArrowDownIcon, ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import StatusBadge from '../components/StatusBadge';
import Money from '../components/Money';
import EmptyState from '../components/EmptyState';
import { fetchB2bOrders } from '../api/marketplaceApi';

type SortField = 'created_at' | 'total_amount' | 'delivery_date';
type SortDir = 'ASC' | 'DESC';

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'awaiting_confirmation', label: 'Awaiting Confirmation' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'partially_fulfilled', label: 'Partially Fulfilled' },
  { value: 'fulfilled', label: 'Fulfilled' },
  { value: 'partially_delivered', label: 'Partially Delivered' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'disputed', label: 'Disputed' },
];

const BuyerOrders: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('DESC');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, any> = {
        page,
        limit,
        sort: sortField,
        order: sortDir,
      };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;

      const res = await fetchB2bOrders(params);
      const orderList = res.orders || res.items || [];
      setOrders(orderList);
      setTotal(res.total || orderList.length);
    } catch (err: any) {
      setError(err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, sortField, sortDir, search]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [statusFilter, sortField, sortDir, search]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(field);
      setSortDir('DESC');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDir === 'ASC'
      ? <ArrowUpIcon className="w-3 h-3 inline ml-1" />
      : <ArrowDownIcon className="w-3 h-3 inline ml-1" />;
  };

  // Client-side search filter (for instant feedback)
  const filteredOrders = orders.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (o.order_number || '').toLowerCase().includes(q) ||
      (o.buyer_org_name || '').toLowerCase().includes(q) ||
      (o.supplier_name || '').toLowerCase().includes(q) ||
      (o.items || []).some((i: any) => (i.title || '').toLowerCase().includes(q))
    );
  });

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Purchase Orders</h2>
          <p className="text-sm text-gray-500 mt-0.5">{total} order{total !== 1 ? 's' : ''} total</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => loadOrders()}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => navigate('/marketplace/buyer/catalogue')}
            className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700"
          >
            <ShoppingCartIcon className="w-4 h-4" /> Continue Shopping
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex flex-wrap gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by order number, supplier, product..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {/* Sort */}
          <select
            value={`${sortField}-${sortDir}`}
            onChange={e => {
              const [f, d] = e.target.value.split('-') as [SortField, SortDir];
              setSortField(f);
              setSortDir(d);
            }}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
          >
            <option value="created_at-DESC">Newest First</option>
            <option value="created_at-ASC">Oldest First</option>
            <option value="total_amount-DESC">Highest Value</option>
            <option value="total_amount-ASC">Lowest Value</option>
            <option value="delivery_date-ASC">Delivery Date (Soonest)</option>
          </select>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between">
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={loadOrders} className="text-sm font-medium text-red-600 hover:underline">Retry</button>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <ArrowPathIcon className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      ) : filteredOrders.length === 0 ? (
        <EmptyState
          icon={<DocumentDuplicateIcon className="w-12 h-12" />}
          title={search || statusFilter ? 'No orders match your filters' : 'No purchase orders yet'}
          description={search || statusFilter ? 'Try adjusting your search or filters.' : 'Orders will appear here when you place an order or accept a quotation.'}
          action={!search && !statusFilter ? { label: 'Browse Marketplace', onClick: () => navigate('/marketplace/buyer/catalogue') } : undefined}
        />
      ) : (
        <>
          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium text-gray-600">Order #</th>
                    <th className="px-4 py-2.5 text-left font-medium text-gray-600">Date</th>
                    <th className="px-4 py-2.5 text-left font-medium text-gray-600">Suppliers</th>
                    <th className="px-4 py-2.5 text-left font-medium text-gray-600">Items</th>
                    <th className="px-4 py-2.5 text-left font-medium text-gray-600 cursor-pointer select-none" onClick={() => toggleSort('total_amount')}>
                      Total <SortIcon field="total_amount" />
                    </th>
                    <th className="px-4 py-2.5 text-left font-medium text-gray-600">Payment</th>
                    <th className="px-4 py-2.5 text-left font-medium text-gray-600 cursor-pointer select-none" onClick={() => toggleSort('delivery_date')}>
                      Delivery <SortIcon field="delivery_date" />
                    </th>
                    <th className="px-4 py-2.5 text-left font-medium text-gray-600">Status</th>
                    <th className="px-4 py-2.5 text-left font-medium text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredOrders.map((o: any) => (
                    <tr
                      key={o.id}
                      className="hover:bg-gray-50 cursor-pointer group"
                      onClick={() => navigate(`/marketplace/buyer/orders/${o.id}`)}
                    >
                      <td className="px-4 py-3 font-mono text-xs font-medium text-gray-900">{o.order_number}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {new Date(o.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {o.supplier_count || o.supplierOrders?.length || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {o.item_count || o.items?.length || 0}
                      </td>
                      <td className="px-4 py-3 font-medium text-sm">
                        <Money amount={o.total_amount} currency={o.currency} />
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs truncate max-w-[120px]">
                        {o.payment_terms || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {o.delivery_date ? new Date(o.delivery_date).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={o.status} size="sm" />
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/marketplace/buyer/orders/${o.id}`); }}
                          className="text-xs font-medium text-emerald-600 hover:text-emerald-700 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          View →
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm font-medium border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="px-3 py-1.5 text-sm text-gray-500">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 text-sm font-medium border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BuyerOrders;
